import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { LogOut, Menu, UserCircle, UserCog } from "lucide-react";
import { Breadcrumbs } from "./breadcrumbs";
import { NotificationBell } from "@/components/layout/notification-bell";
import { useAuth } from "@/components/providers/auth-provider";

interface HeaderProps {
  onMenuClick: () => void;
}

export function Header({ onMenuClick }: HeaderProps) {
  const { user, isAdmin, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Cerrar el dropdown al hacer click fuera o pulsar Escape.
  useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [menuOpen]);

  return (
    <header className="bg-surface border-b border-line px-4 lg:px-6 h-14 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <button
          onClick={onMenuClick}
          className="md:hidden p-2 text-content-muted hover:text-ink hover:bg-surface-100 rounded-md transition-colors"
          aria-label="Abrir menú"
        >
          <Menu className="w-5 h-5" />
        </button>
        <Breadcrumbs />
      </div>
      <div className="flex items-center gap-2">
        <NotificationBell />
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="p-1 text-content-muted hover:text-ink transition-colors"
            aria-label="Perfil de usuario"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
          >
            <UserCircle className="w-8 h-8" />
          </button>
          {menuOpen && (
            <div
              role="menu"
              className="absolute right-0 mt-2 w-64 bg-surface border border-line rounded-md shadow-card z-50 overflow-hidden"
            >
              {user && (
                <div className="px-4 py-3 border-b border-line">
                  <div className="text-sm font-heading font-semibold text-ink truncate">
                    {user.name || user.email}
                  </div>
                  {user.name && (
                    <div className="text-xs text-content-muted truncate">
                      {user.email}
                    </div>
                  )}
                  {isAdmin && (
                    <span className="inline-block mt-1 px-2 py-0.5 text-[10px] font-heading font-semibold uppercase tracking-wider bg-brand/12 text-brand-hover rounded-pill">
                      Administrador
                    </span>
                  )}
                </div>
              )}
              <Link
                to="/configuracion/perfil"
                onClick={() => setMenuOpen(false)}
                role="menuitem"
                className="flex items-center gap-2 px-4 py-2 text-sm text-content hover:bg-surface-100 transition-colors"
              >
                <UserCog className="w-4 h-4 text-content-muted" />
                Mi perfil
              </Link>
              <button
                onClick={() => {
                  setMenuOpen(false);
                  logout();
                }}
                role="menuitem"
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-danger hover:bg-danger/8 border-t border-line transition-colors"
              >
                <LogOut className="w-4 h-4" />
                Cerrar sesión
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
